import { useMemo } from 'react';
import { ArrowRightAlt, Delete, Edit, FileCopy, Search } from '@mui/icons-material';
import { Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAdminScenariosQuery } from '../../api/gql';
import { renderCellOrDefault } from '../../components/table/renderCellOrDefault';
import { Table } from '../../components/table/Table';
import { useControlledTableModel } from '../../hooks/useControlledTableModel';
import { scenarioPlanToTransMap } from '../../utils/maps';

const TRANS_NS = 'universal-reminding';

export const ScenariosTable = () => {
  const { t } = useTranslation(TRANS_NS, { keyPrefix: 'scenarios' });

  const { query, debouncedQuery, skip, paginationModel, handlers } = useControlledTableModel({ pageSize: 25 });

  const { data, isLoading } = useAdminScenariosQuery({
    query: debouncedQuery,
    take: 25,
    skip: skip,
  });

  const rows = useMemo(
    () =>
      data?.adminScenarios.results
        .filter((s) => !s.static)
        .map((scenario) => ({
          id: scenario.id,
          name: scenario.name,
          user: scenario.user?.lastName ? `${scenario.user?.firstName} ${scenario.user?.lastName}` : t('system'),
          model: scenario.model.name,
          plan: t(scenarioPlanToTransMap[scenario.plan.name]) as string,
          isSystem: !scenario.user?.lastName,
        })) ?? [],
    [data?.adminScenarios.results, t],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('form.name') as string,
        minWidth: 150,
        flex: 1,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'model',
        headerName: t('form.model') as string,
        flex: 1,
        minWidth: 140,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'user',
        headerName: t('form.user') as string,
        flex: 1,
        minWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'plan',
        headerName: t('form.plan') as string,
        flex: 1,
        minWidth: 230,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'id',
        headerName: '',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        align: 'right',
        minWidth: 300,
        renderCell: (params) =>
          params.row.isSystem ? (
            <Box width={'100%'} display={'flex'} justifyContent={'center'}>
              <Typography variant={'caption'} color={'text.secondary'}>
                {t('doNotEditSystem')}
              </Typography>
            </Box>
          ) : (
            <Stack direction={'row'} gap={1}>
              <IconButton
                component={Link}
                to={`/admin/universal-reminding/scenarios/delete/${params.id}`}
                title={t('action.delete') as string}
              >
                <Delete color={'action'} />
              </IconButton>
              <IconButton
                component={Link}
                to={`/admin/universal-reminding/scenarios/duplicate/${params.id}`}
                title={t('action.duplicate') as string}
              >
                <FileCopy color={'action'} />
              </IconButton>
              <IconButton
                component={Link}
                to={`/admin/universal-reminding/scenarios/edit/${params.id}`}
                title={t('edit') as string}
              >
                <Edit color={'action'} />
              </IconButton>
              <Button
                variant={'outlined'}
                component={Link}
                to={`/admin/universal-reminding/scenarios/${params.id}`}
                endIcon={<ArrowRightAlt />}
                title={t('action.detail') as string}
              >
                {t('action.detail')}
              </Button>
            </Stack>
          ),
      },
    ],
    [t],
  );

  return (
    <Stack height={'100%'} width={'100%'} gap={2}>
      <TextField
        fullWidth
        placeholder={t('action.search') ?? ''}
        onChange={(e) => handlers.setQuery(e.target.value)}
        value={query}
        InputProps={{
          sx: { bgcolor: 'background.paper' },
          startAdornment: (
            <InputAdornment position={'start'}>
              <Search color={'primary'} />
            </InputAdornment>
          ),
        }}
      />
      <Table
        id={'admin-scenarios'}
        columns={columns}
        rows={rows}
        loading={isLoading}
        paginationModel={paginationModel}
      />
    </Stack>
  );
};
